/* ==========================================================================
   we-pdp-gallery.js - New PDP Gallery behaviour, split out of we-pdp.js.
   --------------------------------------------------------------------------
   Previously the Gallery's slide/thumbnail interaction lived inside the
   shared we-pdp.js closure. That was fine while Gallery never needed to
   talk to anything else on the page. It now needs to react to the buy box's
   selected variant, and a CustomEvent ("wep:variant-change", dispatched by
   we-pdp.js) is a clean, decoupled boundary for that - which is what makes
   a real file split safe now: this file only ever listens for that one
   event, it never reaches into we-pdp.js's state directly, and vice versa.

   Own idempotent bind (window.WePdpGallery), own delegated listener, no
   globals beyond that one namespace - same constraints as we-pdp.js.
   ========================================================================== */
(function () {
  'use strict';

  if (window.WePdpGallery && window.WePdpGallery.bound) {
    window.WePdpGallery.refresh();
    return;
  }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* ------------------------------------------------------------------------
     Live-DOM indexing
     ------------------------------------------------------------------------
     data-wep-slide-id / data-wep-slide are re-derived from DOM order after
     every structural change (initial load, variant swap) instead of being
     trusted from however Liquid numbered them at render time. That is what
     lets a variant swap just insert/remove markup without having to also
     recompute what every index "should" be - whatever order the nodes end
     up in IS the order, applyVariantGallery() below relies on this to stay
     correct without any special-casing.
     ------------------------------------------------------------------------ */
  function thumbCarouselRoot(gallery) {
    return qs('[data-wep-gallery-thumbs]', gallery);
  }

  function reindexGallery(gallery) {
    var slides = qsa('.we-pdp__slide', gallery);
    var thumbs = qsa('[data-wep-slide]', gallery);

    slides.forEach(function (slide, i) {
      slide.dataset.wepSlideId = String(i);
    });
    thumbs.forEach(function (thumb, i) {
      thumb.dataset.wepSlide = String(i);
    });

    var thumbsRoot = thumbCarouselRoot(gallery);
    if (thumbsRoot) {
      /* >4 items: thumbnails get a fixed width sized as "if there were
         exactly 4" (we-pdp-hero.css) so the row overflows into native
         horizontal scroll instead of shrinking - see that file's comment.
         <=4: unmodified flex:1, identical to the original approved layout. */
      if (thumbs.length > 4) {
        thumbsRoot.setAttribute('data-wep-thumb-scroll', '');
      } else {
        thumbsRoot.removeAttribute('data-wep-thumb-scroll');
      }
    }
  }

  function activeIndex(gallery) {
    var activeThumb = qs('.we-pdp__thumb--active', gallery);
    if (activeThumb) return parseInt(activeThumb.dataset.wepSlide, 10);
    var activeSlide = qs('.we-pdp__slide--active', gallery);
    if (activeSlide) return parseInt(activeSlide.dataset.wepSlideId, 10);
    return 0;
  }

  function updateNavState(gallery) {
    var total = qsa('[data-wep-slide]', gallery).length;
    var idx = activeIndex(gallery);
    var prevBtn = qs('[data-wep-stage-prev]', gallery);
    var nextBtn = qs('[data-wep-stage-next]', gallery);
    if (prevBtn) prevBtn.disabled = total === 0 || idx <= 0;
    if (nextBtn) nextBtn.disabled = total === 0 || idx >= total - 1;
  }

  function updateCounter(gallery) {
    var counter = qs('[data-wep-media-counter]', gallery);
    if (!counter) return;
    var total = qsa('[data-wep-slide]', gallery).length;
    counter.textContent = total ? (activeIndex(gallery) + 1) + ' of ' + total : '';
  }

  function updateEmptyState(gallery) {
    var note = qs('[data-wep-gallery-empty]', gallery);
    if (!note) return;
    note.hidden = qsa('[data-wep-slide]', gallery).length > 0;
  }

  function stageEl(gallery) {
    return qs('[data-wep-stage]', gallery);
  }

  /* Moves the track with a direct scrollLeft write, for exactly the reason
     spelled out on scrollThumbIntoView below: scrollIntoView is specified to
     walk every scrollable ancestor up to the document, which is what once let
     a horizontal gallery scroll bleed into a page-level vertical jump. Setting
     scrollLeft touches only this element, so it structurally cannot move the
     page. Each slide is exactly one track width, so the target offset is just
     index * clientWidth - no measuring of individual slides needed.

     The <1px early return keeps a programmatic move from re-entering the
     scroll handler when the track is already where it should be. */
  function scrollStageToIndex(gallery, index) {
    var stage = stageEl(gallery);
    if (!stage) return;

    var width = stage.clientWidth;
    if (!width) return;

    var target = index * width;
    if (Math.abs(stage.scrollLeft - target) < 1) return;

    if (stage.scrollTo) {
      stage.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    } else {
      stage.scrollLeft = target;
    }
  }

  /* Deliberately not thumb.scrollIntoView({block:'nearest', inline:'nearest'})
     - that used to be here, and even with block:'nearest' asking it not to
     move the vertical axis, scrollIntoView is specified to walk every
     scrollable ancestor up to the document and adjust each one as needed to
     satisfy the request; "nearest" only controls the alignment it aims for
     in each of those boxes, it doesn't confine which boxes get scrolled.
     That's what let a horizontal thumbnail-strip scroll bleed into an
     actual page-level vertical scroll on click. Computing and setting
     .we-pdp__thumbs's own scrollLeft directly can't do that: it only ever
     touches the one element it's called on, never an ancestor, so it
     structurally can't move the document. */
  function scrollThumbIntoView(gallery, index) {
    var thumbsRow = qs('.we-pdp__thumbs', gallery);
    var thumb = qsa('[data-wep-slide]', gallery).filter(function (t) {
      return parseInt(t.dataset.wepSlide, 10) === index;
    })[0];
    if (!thumbsRow || !thumb) return;

    var thumbLeft = thumb.offsetLeft;
    var thumbRight = thumbLeft + thumb.offsetWidth;
    var viewLeft = thumbsRow.scrollLeft;
    var viewRight = viewLeft + thumbsRow.clientWidth;

    var target = null;
    if (thumbLeft < viewLeft) {
      target = thumbLeft;
    } else if (thumbRight > viewRight) {
      target = thumbRight - thumbsRow.clientWidth;
    }
    if (target === null) return;

    if (thumbsRow.scrollTo) {
      thumbsRow.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    } else {
      thumbsRow.scrollLeft = target;
    }
  }

  function selectSlideByIndex(gallery, index, opts) {
    var thumbs = qsa('[data-wep-slide]', gallery);
    var total = thumbs.length;
    if (!total) {
      updateNavState(gallery);
      updateCounter(gallery);
      return;
    }
    index = Math.max(0, Math.min(index, total - 1));

    thumbs.forEach(function (btn) {
      var active = parseInt(btn.dataset.wepSlide, 10) === index;
      btn.classList.toggle('we-pdp__thumb--active', active);
      btn.setAttribute('aria-selected', String(active));
    });

    qsa('.we-pdp__slide', gallery).forEach(function (slide) {
      slide.classList.toggle('we-pdp__slide--active', parseInt(slide.dataset.wepSlideId, 10) === index);
    });

    /* opts.scrollStage is set false by the scroll handler only: there the
       user's own gesture IS the movement, so re-driving the track from here
       would fight the gesture and feed back into the handler that called us. */
    if (!opts || opts.scrollStage !== false) scrollStageToIndex(gallery, index);
    if (!opts || opts.scrollThumb !== false) scrollThumbIntoView(gallery, index);
    updateNavState(gallery);
    updateCounter(gallery);
  }

  function stepSlide(gallery, delta) {
    selectSlideByIndex(gallery, activeIndex(gallery) + delta);
  }

  /* ------------------------------------------------------------------------
     Variant swap
     ------------------------------------------------------------------------
     [data-wep-gallery-variants] holds every OTHER variant's pre-rendered
     slide/thumb markup (we-pdp-gallery.liquid), keyed by variant id, as
     inert JSON text - nothing in it is live DOM until swapped in here.
     Only the variant-sourced nodes (data-wep-variant-item) are ever
     removed/replaced; the generic ones (data-wep-generic-item) are never
     touched, so generic media never needs to be re-sent or re-parsed.
     ------------------------------------------------------------------------ */
  function getVariantData(gallery) {
    if (gallery.__wepVariantData) return gallery.__wepVariantData;
    var script = qs('[data-wep-gallery-variants]', gallery);
    var data = {};
    if (script) {
      try {
        data = JSON.parse(script.textContent) || {};
      } catch (e) {
        data = {};
      }
    }
    gallery.__wepVariantData = data;
    return data;
  }

  function replaceVariantItems(container, html) {
    if (!container) return;
    qsa('[data-wep-variant-item]', container).forEach(function (node) {
      node.remove();
    });

    var genericAnchor = qs('[data-wep-generic-item]', container);
    var tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    Array.prototype.slice.call(tmp.children).forEach(function (node) {
      container.insertBefore(node, genericAnchor || null);
    });
  }

  function applyVariantGallery(gallery, variantId) {
    var entry = getVariantData(gallery)[String(variantId)];
    var stage = qs('.we-pdp__stage', gallery);
    var thumbsRow = qs('.we-pdp__thumbs', gallery);
    if (!stage || !thumbsRow) return;

    replaceVariantItems(stage, entry && entry.slides_html);
    replaceVariantItems(thumbsRow, entry && entry.thumbs_html);

    reindexGallery(gallery);
    selectSlideByIndex(gallery, 0, { scrollThumb: false, scrollStage: false });
    thumbsRow.scrollLeft = 0;
    /* Direct assignment rather than scrollStageToIndex: a variant change should
       land on the first slide immediately, not animate across the old ones. */
    stage.scrollLeft = 0;
    updateEmptyState(gallery);
  }

  function onVariantChange(event) {
    var variantId = event && event.detail && event.detail.variantId;
    if (!variantId) return;
    qsa('[data-wep-gallery]').forEach(function (gallery) {
      applyVariantGallery(gallery, variantId);
    });
  }

  /* ------------------------------------------------------------------------
     Native scroll -> active index
     ------------------------------------------------------------------------
     The track is scrolled by the browser (touch swipe, trackpad, keyboard on
     the focused track), never by gesture JS - there are no touch or pointer
     listeners in this file. This reads the resulting scroll position back and
     funnels it through the same selectSlideByIndex() every click already uses,
     so the thumbnail, its aria-selected, the arrows and the counter all update
     from one place. No second active-index source of truth is introduced.

     Because every slide is exactly one track width, the index is just
     round(scrollLeft / clientWidth) - deterministic, and immune to the
     several-slides-visible ambiguity a threshold observer would have.

     Bound to the track element, not to document, and passive so it can never
     delay the scroll itself. rAF-coalesced because scroll fires far more often
     than the index actually changes.
     ------------------------------------------------------------------------ */
  function initStageScrollSync(gallery) {
    var stage = stageEl(gallery);
    if (!stage || stage.dataset.wepStageSync === 'true') return;
    stage.dataset.wepStageSync = 'true';

    var pending = false;

    stage.addEventListener('scroll', function () {
      if (pending) return;
      pending = true;

      window.requestAnimationFrame(function () {
        pending = false;

        var width = stage.clientWidth;
        if (!width) return;

        var index = Math.round(stage.scrollLeft / width);
        if (index === activeIndex(gallery)) return;

        selectSlideByIndex(gallery, index, { scrollStage: false });
      });
    }, { passive: true });
  }

  /* ------------------------------------------------------------------------
     Delegated events - bound once
     ------------------------------------------------------------------------ */
  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var thumb = target.closest('[data-wep-slide]');
    if (thumb) {
      var gallery = thumb.closest('[data-wep-gallery]');
      if (gallery) selectSlideByIndex(gallery, parseInt(thumb.dataset.wepSlide, 10));
      return;
    }

    var prevBtn = target.closest('[data-wep-stage-prev]');
    if (prevBtn) {
      var prevGallery = prevBtn.closest('[data-wep-gallery]');
      if (prevGallery) stepSlide(prevGallery, -1);
      return;
    }

    var nextBtn = target.closest('[data-wep-stage-next]');
    if (nextBtn) {
      var nextGallery = nextBtn.closest('[data-wep-gallery]');
      if (nextGallery) stepSlide(nextGallery, 1);
    }
  }

  /* ------------------------------------------------------------------------
     Init / refresh
     ------------------------------------------------------------------------ */
  function refresh() {
    qsa('[data-wep-gallery]').forEach(function (gallery) {
      reindexGallery(gallery);
      initStageScrollSync(gallery);
      updateNavState(gallery);
      updateCounter(gallery);
      updateEmptyState(gallery);
    });
  }

  document.addEventListener('click', onClick);
  document.addEventListener('wep:variant-change', onVariantChange);
  document.addEventListener('shopify:section:load', refresh);

  window.WePdpGallery = { bound: true, refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
